import { Button } from '@amglobal/ui-shared'
import { Link } from 'react-router-dom'
import { isNonProductionWorkspace } from '@/config/public-env'
import { toReference } from '@/shared/api/localized-error'
import { isStaleChunkLoadError } from '@/shared/lib/chunk-load-recovery'
import { useTranslation } from '@/shared/i18n/use-translation'

export interface AppErrorFallbackProps {
  error?: Error | null
  traceId?: string
}

export function AppErrorFallback({ error, traceId }: AppErrorFallbackProps) {
  const { t } = useTranslation()
  const reference = toReference(traceId)
  const staleChunk = isStaleChunkLoadError(error)
  const showErrorDetails = import.meta.env.DEV || isNonProductionWorkspace()

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 px-6 text-center">
      <h1 className="text-xl font-semibold text-foreground">{t('errors.boundary.title')}</h1>
      <p className="max-w-md text-sm text-muted-foreground">
        {staleChunk
          ? 'This page failed to load after a recent update. Reload the tab to fetch the latest version, then try again.'
          : t('errors.boundary.description')}
      </p>
      {showErrorDetails && error?.message ? (
        <p className="max-w-lg break-words font-mono text-xs text-destructive">{error.message}</p>
      ) : null}
      {reference ? (
        <p className="text-xs text-muted-foreground">
          {t('errors.reference', { ref: reference })}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center justify-center gap-2">
        {staleChunk ? (
          <Button type="button" onClick={() => window.location.reload()}>
            Reload page
          </Button>
        ) : null}
        <Link to="/">
          <Button variant="secondary">{t('errors.boundary.home')}</Button>
        </Link>
      </div>
    </div>
  )
}
